import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent } from 'react';
import * as monaco from 'monaco-editor';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  useIntegration,
  useSaveCodeMutation,
  useUpdateIntegrationFields,
  useUpdateTriggersMutation,
  useClearIntegrationPolls,
  useClearIntegrationKVStore,
  useUpdateIntegrationKey,
} from '@/hooks/integrations';
import { useAuthStore } from '@/store/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import { useDeleteIntegrationMutation } from '@/hooks/integrations';
import DeviceVariablesForm from '@/features/Devices/IntegrationVariables/DeviceForm';
import {
  IntegrationField,
  PartialIntegrationFieldDto,
} from '@/lib/integrationTypes/integrationTypes';
import { PageTitle } from '@/components/PageTitle';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { parseJSON, formatDistanceToNow } from 'date-fns';
import { Check, GripVertical, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type IntegrationPageParams = {
  id: string;
};

const MonacoEditor = lazy(async () => await import('@/components/MonacoEditor'));

const COMMON_URI_PROTOCOLS = [
  { label: 'HTTPS', value: 'https://' },
  { label: 'HTTP', value: 'http://' },
  { label: 'RTSP', value: 'rtsp://' },
  { label: 'RTSPS', value: 'rtsps://' },
  { label: 'RTMP', value: 'rtmp://' },
  { label: 'WebSocket', value: 'ws://' },
  { label: 'Secure WS', value: 'wss://' },
];

const getTypeTagStyle = (type: string): string => {
  const styles: Record<string, string> = {
    string: 'bg-blue-500/20 text-blue-200',
    number: 'bg-green-500/20 text-green-200',
    boolean: 'bg-purple-500/20 text-purple-200',
    password: 'bg-orange-500/20 text-orange-200',
    select: 'bg-cyan-500/20 text-cyan-200',
    uri: 'bg-teal-500/20 text-teal-200',
  };
  return styles[type] || 'bg-gray-500/20 text-gray-200';
};

const ReadOnlyCodeViewer = ({ value, language = 'javascript' }: { value?: string | null; language?: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const instance = monaco.editor.create(containerRef.current, {
      value: value || '',
      language,
      theme: 'vs-dark',
      readOnly: true,
      automaticLayout: true,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      lineNumbers: 'on',
    });
    editorRef.current = instance;

    return () => {
      instance.dispose();
      editorRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (editorRef.current && value != null) {
      editorRef.current.setValue(value);
    }
  }, [value]);

  return <div ref={containerRef} className="flex-grow" />;
};

const IntegrationPage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const roles = useAuthStore((state) => state.roles);
  const isSuperAdmin = roles.includes('SuperAdmin');

  const { id } = useParams<IntegrationPageParams>();
  const [newVarName, setNewVarName] = useState('');
  const [newVarType, setNewVarType] = useState('');
  const [newVarDescription, setNewVarDescription] = useState('');
  const [newVarDocUrl, setNewVarDocUrl] = useState('');
  const [newVarOptionInput, setNewVarOptionInput] = useState('');
  const [newVarOptions, setNewVarOptions] = useState<string[]>([]);
  const [newVarMultiple, setNewVarMultiple] = useState(false);
  const [newVarOptional, setNewVarOptional] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<IntegrationField | null>(null);
  const [editOptionInput, setEditOptionInput] = useState('');
  const [draggingNewOptionIdx, setDraggingNewOptionIdx] = useState<number | null>(null);
  const [draggingEditOptionIdx, setDraggingEditOptionIdx] = useState<number | null>(null);
  const [isEditingKey, setIsEditingKey] = useState(false);
  const [keyDraft, setKeyDraft] = useState('');

  const reorderList = <T,>(list: T[], from: number, to: number) => {
    const updated = [...list];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    return updated;
  };

  const sanitizeOptions = (
    options?: (string | null | undefined)[] | null
  ): string[] | undefined => {
    if (!options) {
      return undefined;
    }

    const trimmed = options
      .map((option) => (option ?? '').trim())
      .filter((option) => option.length > 0);

    if (trimmed.length === 0) {
      return undefined;
    }

    return Array.from(new Set(trimmed));
  };

  const handleAddNewVarOption = () => {
    const trimmedOption = newVarOptionInput.trim();
    if (!trimmedOption) {
      return;
    }

    setNewVarOptions((prev) => (prev.includes(trimmedOption) ? prev : [...prev, trimmedOption]));
    setNewVarOptionInput('');
  };

  useEffect(() => {
    // Keep options for select (dropdown choices) and uri (allowed protocols)
    if (newVarType !== 'select' && newVarType !== 'uri' && (newVarOptions.length > 0 || newVarOptionInput)) {
      setNewVarOptions([]);
      setNewVarOptionInput('');
    }
  }, [newVarType, newVarOptions.length, newVarOptionInput]);

  const handleRemoveNewVarOption = (option: string) => {
    setNewVarOptions((prev) => prev.filter((item) => item !== option));
    setDraggingNewOptionIdx(null);
  };

  const handleNewVarOptionKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddNewVarOption();
    }
  };

  const handleNewOptionDragStart = (index: number) => {
    setDraggingNewOptionIdx(index);
  };

  const handleNewOptionDragOver = (event: DragEvent<HTMLDivElement>, targetIndex: number) => {
    event.preventDefault();
    setNewVarOptions((prev) => {
      if (draggingNewOptionIdx === null || draggingNewOptionIdx === targetIndex) {
        return prev;
      }
      const updated = reorderList(prev, draggingNewOptionIdx, targetIndex);
      setDraggingNewOptionIdx(targetIndex);
      return updated;
    });
  };

  const handleNewOptionDragEnd = () => {
    setDraggingNewOptionIdx(null);
  };

  const handleAddEditOption = () => {
    const trimmedOption = editOptionInput.trim();
    if (!trimmedOption) {
      return;
    }

    setSelectedVariable((prev) => {
      if (!prev) {
        return prev;
      }

      const existing = prev.options ?? [];
      if (existing.includes(trimmedOption)) {
        return prev;
      }

      return {
        ...prev,
        options: [...existing, trimmedOption],
      };
    });

    setEditOptionInput('');
  };

  const handleRemoveEditOption = (option: string) => {
    setSelectedVariable((prev) => {
      if (!prev) {
        return prev;
      }

      const existing = prev.options ?? [];

      return {
        ...prev,
        options: existing.filter((item) => item !== option),
      };
    });
    setDraggingEditOptionIdx(null);
  };

  const handleEditOptionKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddEditOption();
    }
  };

  const handleEditOptionDragStart = (index: number) => {
    setDraggingEditOptionIdx(index);
  };

  const handleEditOptionDragOver = (event: DragEvent<HTMLDivElement>, targetIndex: number) => {
    event.preventDefault();
    setSelectedVariable((prev) => {
      if (!prev || draggingEditOptionIdx === null || draggingEditOptionIdx === targetIndex) {
        return prev;
      }

      const options = prev.options ?? [];
      const updatedOptions = reorderList(options, draggingEditOptionIdx, targetIndex);
      setDraggingEditOptionIdx(targetIndex);
      return {
        ...prev,
        options: updatedOptions,
      };
    });
  };

  const handleEditOptionDragEnd = () => {
    setDraggingEditOptionIdx(null);
  };
  const [currentCode, setCurrentCode] = useState('');
  const [lastSavedDisplay, setLastSavedDisplay] = useState('');
  const [openModal, setOpenModal] = useState(false);

  const {
    isPending: saving,
    mutateAsync: uploadCode,
    data: uploadResponse,
  } = useSaveCodeMutation({
    id: id ?? 'undefined',
  });

  const { mutateAsync: updateIntegrationTriggers, isPending: updatingIntergrationTriggers } =
    useUpdateTriggersMutation({
      id: id ?? 'undefined',
      queryClient,
    });

  const {
    isLoading: integrationLoading,
    isFetching: integrationFetching,
    data: integrationResponse,
    isError: isIntegrationError,
    error: integrationError,
  } = useIntegration({
    id: id ?? 'undefined',
  });

  const { mutateAsync: updateIntegrationFields } = useUpdateIntegrationFields({
    queryClient,
    id: id ?? 'undefined',
  });

  const { mutateAsync: clearPolls, isPending: clearingPolls } = useClearIntegrationPolls({
    id: id ?? '',
  });

  const { mutateAsync: clearKVStore, isPending: clearingKVStore } = useClearIntegrationKVStore({
    id: id ?? '',
  });

  const { mutateAsync: updateIntegrationKey, isPending: updatingKey } = useUpdateIntegrationKey({
    id: id ?? '',
    queryClient,
  });

  const handleSaveKey = async () => {
    const trimmed = keyDraft.trim();
    try {
      await updateIntegrationKey({ key: trimmed || null });
      toast({ title: 'Integration key updated' });
      setIsEditingKey(false);
    } catch (ex: any) {
      toast({
        variant: 'destructive',
        title: 'Failed to update key',
        description: ex?.message || 'Unknown error',
      });
    }
  };

  const updateLastSaved = useCallback(() => {
    console.log(
      uploadResponse?.integration?.updated,
      integrationResponse?.integration?.updated,
      integrationResponse?.integration?.created
    );
    let last_updated =
      uploadResponse?.integration?.updated ||
      integrationResponse?.integration?.updated ||
      integrationResponse?.integration?.created;
    if (!last_updated) {
      setLastSavedDisplay('Never saved');
      return;
    }

    const timeSeconds = formatDistanceToNow(parseJSON(last_updated));
    setLastSavedDisplay(`Saved ${timeSeconds} ago`);
  }, [integrationResponse, uploadResponse]);

  useEffect(() => {
    if (saving) {
      setLastSavedDisplay('Saving');
      return;
    }

    updateLastSaved();
    const interval = setInterval(updateLastSaved, 60_000);

    return () => {
      clearInterval(interval);
    };
  }, [saving, updateLastSaved]);

  const handleAddVariable = () => {
    if (!newVarName || !newVarType || !integrationResponse) return;

    const requiresOptions = newVarType === 'select';
    const supportsOptions = newVarType === 'select' || newVarType === 'uri';
    const sanitizedNewOptions = sanitizeOptions(newVarOptions);
    if (!newVarOptional && requiresOptions && (!sanitizedNewOptions || sanitizedNewOptions.length === 0)) {
      toast({
        title: 'Options required',
        description: 'Please add at least one option when selecting the Select data type.',
        variant: 'destructive',
      });
      return;
    }

    const dataType = newVarMultiple ? `${newVarType}[]` : newVarType;
    const newVariable: PartialIntegrationFieldDto = {
      name: newVarName,
      dataType,
      description: newVarDescription || undefined,
      documentation: newVarDocUrl || undefined,
      options: supportsOptions ? sanitizedNewOptions : undefined,
      optional: newVarOptional || undefined,
    };

    const existingFields = integrationResponse.integration.integrationFields.map((field) => {
      const sanitized = sanitizeOptions(field.options);
      return {
        ...field,
        description: field.description || undefined,
        documentation: field.documentation || undefined,
        options: field.dataType === 'select' ? sanitized ?? [] : sanitized,
      };
    });

    updateIntegrationFields({
      integrationFields: [...existingFields, newVariable],
    });

    setNewVarName('');
    setNewVarType('');
    setNewVarDescription('');
    setNewVarDocUrl('');
    setNewVarOptions([]);
    setNewVarOptionInput('');
    setNewVarMultiple(false);
    setNewVarOptional(false);
  };

  useEffect(() => {
    if (isIntegrationError) {
      toast({
        variant: 'destructive',
        title: 'Could not fetch integration',
        description: integrationError.message,
      });
    }
  }, [isIntegrationError, integrationError]);

  useEffect(() => {
    setEditOptionInput('');
  }, [selectedVariable?.id, selectedVariable?.dataType]);

  const handleUpdateVariable = (updatedVariable: IntegrationField) => {
    if (!integrationResponse) {
      return;
    }

    const sanitizedUpdatedOptions =
      updatedVariable.dataType === 'select'
        ? sanitizeOptions(updatedVariable.options)
        : undefined;

    if (
      updatedVariable.dataType === 'select' &&
      !updatedVariable.optional &&
      (!sanitizedUpdatedOptions || sanitizedUpdatedOptions.length === 0)
    ) {
      toast({
        title: 'Options required',
        description: 'Please keep at least one option for Select data type fields.',
        variant: 'destructive',
      });
      return;
    }

    toast({ title: 'Saving fields' });

    const integrationFields = integrationResponse.integration.integrationFields.map((field) =>
      field.id === updatedVariable.id
        ? {
            ...updatedVariable,
            description: updatedVariable.description || undefined,
            documentation: updatedVariable.documentation || undefined,
            options:
              (updatedVariable.dataType.replace(/\[\]$/, '') === 'select')
                ? sanitizedUpdatedOptions
                : sanitizeOptions(updatedVariable.options),
          }
        : {
            ...field,
            description: field.description || undefined,
            documentation: field.documentation || undefined,
            options:
              (field.dataType.replace(/\[\]$/, '') === 'select')
                ? sanitizeOptions(field.options) ?? []
                : sanitizeOptions(field.options),
          }
    );

    updateIntegrationFields({
      integrationFields,
    })
      .then(() => toast({ title: 'Updated fields' }))
      .catch((ex) => {
        toast({
          title: 'Could not update fields',
          variant: 'destructive',
          description: ex.message,
        });
      });

    setSelectedVariable(null);
  };

  const { mutateAsync: deleteIntegration } = useDeleteIntegrationMutation({
    id: id ?? '',
    queryClient: queryClient,
  });
  const navigate = useNavigate();

  if (integrationLoading) {
    return (
      <div className="flex flex-grow items-center justify-center">
        <PageTitle title="Integration Editor" />

        <div className="flex w-fit flex-row gap-5 p-5 outline-gray-300">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={'animate-spin stroke-gray-50'}
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <span className="text-gray-200">Loading Integration</span>
        </div>
      </div>
    );
  }

  if (isIntegrationError) {
    return (
      <div className="flex flex-grow items-center justify-center">
        <PageTitle title="Integration Editor" />
        <div className="flex w-fit flex-col gap-5 p-5 outline-gray-300">
          <span className="text-gray-200">
            {integrationError instanceof AxiosError && integrationError.status === 404
              ? 'Integration not found'
              : 'Error fetching integration'}
          </span>

          <Link to="/ihub" className={buttonVariants()}>
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  if (!integrationResponse) {
    return (
      <div className="flex flex-grow items-center justify-center">
        <PageTitle title="Integration Editor" />
        <div className="flex w-fit flex-col gap-5 p-5 outline-gray-300">
          <span className="text-gray-200">Unknown error</span>

          <Link to="/ihub" className={buttonVariants()}>
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const integration = integrationResponse.integration;
  const variables = integration.integrationFields;

  return (
    <>
      <PageTitle title={integration.name} />
      <div className="flex items-center justify-between border-b bg-[#1b1b1b]">
        <div className="flex items-center gap-4">
          <Link to="/ihub" className="border-r p-5">
            <img src="/icon/chevron-left.svg" alt="Back" className="h-4 w-4" />
          </Link>
          <h1 className="font-sans text-lg font-bold text-gray-50">Integration Builder</h1>
        </div>
        <div className="flex items-center gap-2 text-white">
          <p className="p-4 text-sm text-gray-400">{lastSavedDisplay}</p>
        </div>
      </div>
      <section className="grid h-[calc(100vh-8rem)] grid-cols-5">
        <div className="h-full overflow-y-auto border-r text-white">
          <Accordion type="single" collapsible defaultValue="details" className="h-full">
            <AccordionItem value="details">
              <AccordionTrigger className="border-b px-4 font-semibold">
                Integration Details
              </AccordionTrigger>
              <AccordionContent>
                <div className="px-4 py-2">
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-gray-400">Name</p>
                    <p className="text-sm">{integration.name}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-gray-400">Type</p>
                    <p className="text-sm">{integration.integrationType}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-gray-400">Key</p>
                    {isEditingKey ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={keyDraft}
                          onChange={(e) => setKeyDraft(e.target.value)}
                          className="h-7 text-sm"
                          placeholder="integration-key-slug"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveKey();
                            }
                            if (e.key === 'Escape') {
                              setIsEditingKey(false);
                            }
                          }}
                          disabled={updatingKey}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleSaveKey}
                          disabled={updatingKey}
                          className="h-7 w-7 p-0"
                        >
                          {updatingKey ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Check className="h-3 w-3 text-green-400" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setIsEditingKey(false)}
                          className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                          disabled={updatingKey}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <p className="text-sm">
                          {integration.key || <span className="text-gray-600">Not set</span>}
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setKeyDraft(integration.key || '');
                            setIsEditingKey(true);
                          }}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    {integration.key && (
                      <p className="mt-1 text-xs text-gray-500">
                        Add <code className="rounded bg-gray-800 px-1">integration:{integration.key}</code> to
                        the <code className="rounded bg-gray-800 px-1">use-synced-integration-code</code> feature
                        flag to use synced code.
                      </p>
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="add-variable">
              <AccordionTrigger className="border-b px-4 font-semibold">
                Add Variable
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 px-4">
                  <div className="flex flex-col gap-4 py-4">
                    <label>
                      <p>Variable Name</p>
                      <input
                        type="text"
                        value={newVarName}
                        onChange={(e) => setNewVarName(e.target.value)}
                        placeholder="Variable Name"
                        className="w-full rounded-md border bg-background px-3 py-2"
                      />
                    </label>
                    <label>
                      <p>Data Type</p>
                      <Select value={newVarType} onValueChange={setNewVarType}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select data type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">
                            <span
                              className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('string')}`}
                            >
                              String
                            </span>
                          </SelectItem>
                          <SelectItem value="number">
                            <span
                              className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('number')}`}
                            >
                              Number
                            </span>
                          </SelectItem>
                          <SelectItem value="boolean">
                            <span
                              className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('boolean')}`}
                            >
                              Boolean
                            </span>
                          </SelectItem>
                          <SelectItem value="password">
                            <span
                              className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('password')}`}
                            >
                              Password
                            </span>
                          </SelectItem>
                          <SelectItem value="select">
                            <span
                              className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('select')}`}
                            >
                              Select
                            </span>
                          </SelectItem>
                          <SelectItem value="uri">
                            <span
                              className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('uri')}`}
                            >
                              URI
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </label>
                    <label>
                      <p> Description (Optional)</p>
                      <input
                        type="text"
                        value={newVarDescription}
                        onChange={(e) => setNewVarDescription(e.target.value)}
                        placeholder="Description"
                        className="w-full rounded-md border bg-background px-3 py-2"
                      />
                    </label>
                    <label>
                      <p>Documentation URL (Optional)</p>
                      <input
                        type="text"
                        value={newVarDocUrl}
                        onChange={(e) => setNewVarDocUrl(e.target.value)}
                        placeholder="Documentation URL"
                        className="w-full rounded-md border bg-background px-3 py-2"
                      />
                    </label>
                    <div className="flex items-center gap-4">
                      {newVarType !== 'select' && (
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="new-var-multiple"
                            checked={newVarMultiple}
                            onCheckedChange={(val) => setNewVarMultiple(val === true)}
                          />
                          <Label htmlFor="new-var-multiple">Multiple</Label>
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="new-var-optional"
                          checked={newVarOptional}
                          onCheckedChange={(val) => setNewVarOptional(val === true)}
                        />
                        <Label htmlFor="new-var-optional">Optional</Label>
                      </div>
                    </div>
                    {newVarType === 'select' && (
                      <label>
                        <p>Options</p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newVarOptionInput}
                            onChange={(e) => setNewVarOptionInput(e.target.value)}
                            onKeyDown={handleNewVarOptionKeyDown}
                            placeholder="Add a selectable option"
                            className="w-full rounded-md border bg-background px-3 py-2"
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={handleAddNewVarOption}
                            disabled={!newVarOptionInput.trim()}
                          >
                            Add
                          </Button>
                        </div>
                        {newVarOptions.length > 0 && (
                          <div className="space-y-2 pt-2">
                            {newVarOptions.map((option, index) => (
                              <div
                                key={option}
                                draggable
                                onDragStart={() => handleNewOptionDragStart(index)}
                                onDragOver={(event) => handleNewOptionDragOver(event, index)}
                                onDragEnd={handleNewOptionDragEnd}
                                className={`flex items-center gap-3 rounded-lg border border-[#3a4b45] bg-[#2e3835] px-3  transition-colors ${
                                  draggingNewOptionIdx === index
                                    ? 'border-lime-400 bg-[#385043] cursor-grabbing'
                                    : 'cursor-pointer hover:border-[#4b5d58]'
                                }`}
                              >
                                <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
                                <span className="flex-1 text-sm text-gray-100">{option}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveNewVarOption(option)}
                                  tooltip="Remove option"
                                >
                                  <Trash2 className="h-4 w-4 text-red-400" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </label>
                    )}
                    {newVarType === 'uri' && (
                      <div className="space-y-3">
                        <div>
                          <p className="mb-2">Allowed Protocols</p>
                          <p className="mb-3 text-xs text-gray-400">
                            Leave empty to allow all common protocols
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {COMMON_URI_PROTOCOLS.map((proto) => {
                              const isSelected = newVarOptions.includes(proto.value);
                              return (
                                <button
                                  key={proto.value}
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      setNewVarOptions((prev) =>
                                        prev.filter((p) => p !== proto.value)
                                      );
                                    } else {
                                      setNewVarOptions((prev) => [...prev, proto.value]);
                                    }
                                  }}
                                  className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                                    isSelected
                                      ? 'border-teal-500 bg-teal-500/20 text-teal-300'
                                      : 'border-[#3a4b45] bg-[#2e3835] text-gray-300 hover:border-[#4b5d58]'
                                  }`}
                                >
                                  {proto.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div>
                          <p className="mb-2">Custom Protocol</p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newVarOptionInput}
                              onChange={(e) => setNewVarOptionInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const trimmed = newVarOptionInput.trim();
                                  if (trimmed && trimmed.includes('://')) {
                                    // Extract just the protocol part (e.g., "https://" from "https://example.com")
                                    const match = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)/);
                                    const protocol = match ? match[1].toLowerCase() : null;
                                    if (protocol && !newVarOptions.includes(protocol)) {
                                      setNewVarOptions((prev) => [...prev, protocol]);
                                    }
                                    setNewVarOptionInput('');
                                  }
                                }
                              }}
                              placeholder="mqtt:// or mqtt://broker.local"
                              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                const trimmed = newVarOptionInput.trim();
                                if (trimmed && trimmed.includes('://')) {
                                  // Extract just the protocol part
                                  const match = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)/);
                                  const protocol = match ? match[1].toLowerCase() : null;
                                  if (protocol && !newVarOptions.includes(protocol)) {
                                    setNewVarOptions((prev) => [...prev, protocol]);
                                  }
                                  setNewVarOptionInput('');
                                }
                              }}
                              disabled={!newVarOptionInput.trim() || !newVarOptionInput.includes('://')}
                            >
                              Add
                            </Button>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">Enter protocol or paste URL to extract protocol</p>
                        </div>
                        {newVarOptions.length > 0 && (
                          <div>
                            <p className="mb-2">Selected Protocols</p>
                            <div className="flex flex-wrap gap-1.5">
                              {newVarOptions.map((option, index) => (
                                <div
                                  key={option}
                                  draggable
                                  onDragStart={() => handleNewOptionDragStart(index)}
                                  onDragOver={(event) => handleNewOptionDragOver(event, index)}
                                  onDragEnd={handleNewOptionDragEnd}
                                  className={`flex items-center gap-1 rounded border border-[#3a4b45] bg-[#2e3835] pl-1.5 pr-0.5 py-0.5 transition-colors ${
                                    draggingNewOptionIdx === index
                                      ? 'border-teal-400 bg-[#1a3a35] cursor-grabbing'
                                      : 'cursor-grab hover:border-[#4b5d58]'
                                  }`}
                                >
                                  <GripVertical className="h-3 w-3 text-gray-500" />
                                  <span className="font-mono text-xs text-gray-100">{option}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveNewVarOption(option)}
                                    className="p-0.5 hover:bg-red-500/20 rounded"
                                  >
                                    <X className="h-3 w-3 text-red-400" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                              Drag to reorder. First protocol is the default.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    <Button
                      className="w-full"
                      onClick={handleAddVariable}
                      disabled={
                        !newVarName ||
                        !newVarType ||
                        (newVarType === 'select' && newVarOptions.length === 0)
                      }
                    >
                      Add Variable
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="triggers">
              <AccordionTrigger className="border-b px-4 font-semibold">Triggers</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-3 p-3">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="smtp-trigger"
                      checked={integration.smtpSource}
                      disabled={updatingIntergrationTriggers || integrationFetching}
                      onCheckedChange={(checked) => {
                        updateIntegrationTriggers({
                          smtp: checked,
                        })
                          .then(() => {
                            toast({ title: 'Triggers updated' });
                          })
                          .catch((ex) => {
                            toast({
                              title: 'Could not update triggers',
                              description:
                                ex instanceof AxiosError
                                  ? 'Error ' + ex.status + ': ' + ex.message
                                  : undefined,
                            });
                          });
                      }}
                    />
                    <Label htmlFor="smtp-trigger">SMTP Trigger</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="webhook-trigger"
                      checked={integration.webhookSource}
                      disabled={updatingIntergrationTriggers || integrationFetching}
                      onCheckedChange={(checked) => {
                        updateIntegrationTriggers({
                          webhook: checked,
                        })
                          .then(() => {
                            toast({ title: 'Triggers updated' });
                          })
                          .catch((ex) => {
                            toast({
                              title: 'Could not update triggers',
                              description:
                                ex instanceof AxiosError
                                  ? 'Error ' + ex.status + ': ' + ex.message
                                  : undefined,
                            });
                          });
                      }}
                    />
                    <Label htmlFor="webhook-trigger">Webhook Trigger</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="smtp-trigger"
                      checked={integration.watchfulLifecycleTrigger}
                      disabled={updatingIntergrationTriggers || integrationFetching}
                      onCheckedChange={(checked) => {
                        updateIntegrationTriggers({
                          watchfulLifecycle: checked,
                        })
                          .then(() => {
                            toast({ title: 'Triggers updated' });
                          })
                          .catch((ex) => {
                            toast({
                              title: 'Could not update triggers',
                              description:
                                ex instanceof AxiosError
                                  ? 'Error ' + ex.status + ': ' + ex.message
                                  : undefined,
                            });
                          });
                      }}
                    />
                    <Label htmlFor="smtp-trigger">Watchful Lifecycle Trigger</Label>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="variables">
              <AccordionTrigger className="border-b px-4 font-semibold">
                Variables ({integrationResponse?.integration.integrationFields.length})
              </AccordionTrigger>
              <AccordionContent>
                <div className="p-2">
                  {variables.length === 0 ? (
                    <div className="p-2 text-sm text-gray-400">No variables added yet</div>
                  ) : (
                    <div className="space-y-2">
                      {variables.map((variable) => (
                        <div
                          key={variable.id}
                          className="flex cursor-pointer items-center justify-between rounded-md border hover:bg-gray-800/50"
                          onClick={() =>
                            setSelectedVariable({
                              ...variable,
                              description: variable.description || undefined,
                              documentation: variable.documentation || undefined,
                              options:
                                variable.dataType === 'select'
                                  ? sanitizeOptions(variable.options) ?? []
                                  : sanitizeOptions(variable.options),
                            })
                          }
                        >
                          <div className="w-full">
                            <div className="flex items-center justify-between">
                              <div className="flex items-start gap-2 p-2">
                                <p className="text-sm font-semibold text-lime">{variable.name}</p>
                                <span
                                  className={`overflow-x-auto whitespace-break-spaces text-wrap rounded-full px-2 py-0.5 text-xs font-semibold ${getTypeTagStyle(variable.dataType)}`}
                                >
                                  {variable.dataType}
                                </span>
                                {variable.optional && (
                                  <span className="rounded-full bg-gray-500/20 px-2 py-0.5 text-xs font-semibold text-gray-300">
                                    Optional
                                  </span>
                                )}
                              </div>
                              {variable.documentation && (
                                <a
                                  href={variable.documentation}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="border-l px-3 py-2 text-xs text-lime hover:text-gray-300"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Docs
                                </a>
                              )}
                            </div>
                            {variable.description && (
                              <p className="line-clamp-2 w-full truncate border-t p-2 text-xs text-gray-500">
                                {variable.description}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="test-connection">
              <AccordionTrigger className="border-b px-4 font-semibold">
                Test Connection
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 p-4">
                  <div className="space-y-4 border-b pb-4">
                    <div className="space-y-2 bg-background/50">
                      {variables.length === 0 ? (
                        <p className="text-sm text-gray-400">No variables defined yet</p>
                      ) : (
                        <DeviceVariablesForm
                          integration={integration}
                          onVariableSubmit={(vars) => {
                            console.log(vars);
                          }}
                          submitText="Test connection"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="m-4 mt-0 border-t pt-4">
                  <p className="mb-2 text-sm text-gray-400">Response</p>
                  <div className="font-mono min-h-[100px] rounded-md border bg-[#111] p-3 text-sm">
                    <p className="text-gray-400">Response will appear here...</p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="review-connection">
              <AccordionTrigger className="border-b px-4 font-semibold">
                Review &amp; Publish
              </AccordionTrigger>
              <AccordionContent>
                <div className="p-4">
                  <div className="flex items-center gap-4 rounded-lg border p-4">
                    <div className="h-4 w-4 rounded-full bg-green-400" />
                    <p>
                      <strong>No issues detected</strong>
                      <br />
                      Ready to publish
                    </p>
                  </div>
                  <Dialog open={openModal} onOpenChange={setOpenModal}>
                    <DialogTrigger asChild>
                      <Button className="mt-4 w-full" disabled={!currentCode}>
                        Publish
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Confirm Publication</DialogTitle>
                        <DialogDescription>
                          Are you sure you want to publish this integration? This will update the
                          live version.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <Button
                          variant="default"
                          disabled={saving}
                          onClick={() => {
                            uploadCode({
                              code: currentCode,
                              recompile: true,
                            })
                              .then((response) => {
                                if (response.success) {
                                  toast({ title: 'Published integration' });
                                } else {
                                  throw new Error('Failed to compile integration');
                                }
                              })
                              .catch((error) => {
                                toast({
                                  title: error.message || 'Failed to publish integration',
                                  variant: 'destructive',
                                });
                              })
                              .finally(() => {
                                setOpenModal(false);
                              });
                          }}
                        >
                          {saving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Publishing...
                            </>
                          ) : (
                            'Publish'
                          )}
                        </Button>
                        <Button variant="outline" onClick={() => setOpenModal(false)} disabled={saving}>
                          Cancel
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="danger-zone" className="sticky top-[100vh] pb-10">
              <AccordionTrigger className="border-t px-4 font-semibold">
                Danger Zone
              </AccordionTrigger>
              <AccordionContent>
                <div className="p-4 space-y-4">
                  {isSuperAdmin && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 ">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" className='destructive' disabled={clearingPolls}>
                            Clear Polls
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle>Clear Scheduled Polls</DialogTitle>
                          </DialogHeader>
                          <DialogDescription>
                            This will clear all scheduled polls for every device with this integration. Use with care.
                            <br/>
                            Are you sure you want to proceed?
                          </DialogDescription>
                          <DialogFooter className="grid gap-4">
                            <DialogClose asChild>
                              <Button
                                variant="destructive"
                                
                                onClick={() =>
                                  clearPolls()
                                    .then(() => toast({ title: 'Polls cleared' }))
                                    .catch((ex) =>
                                      toast({
                                        title: ex?.message || 'Failed to clear polls',
                                        variant: 'destructive',
                                      })
                                    )
                                }
                              >
                                Clear Scheduled Polls
                              </Button>
                            </DialogClose>
                            <DialogClose asChild>
                              <Button variant="default">Cancel</Button>
                            </DialogClose>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" disabled={clearingKVStore}>
                            Clear KV Store
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle>Clear KV Store</DialogTitle>
                          </DialogHeader>
                          <DialogDescription>
                            This will clear the KV Store for every device with this integration. This action is
                            irreversible.
                            <br/>
                            Are you sure you want to proceed?
                          </DialogDescription>
                          <DialogFooter className="grid gap-4">
                            <DialogClose asChild>
                              <Button
                                variant="destructive"
                                onClick={() =>
                                  clearKVStore()
                                    .then(() => toast({ title: 'KV Store cleared' }))
                                    .catch((ex) =>
                                      toast({
                                        title: ex?.message || 'Failed to clear KV Store',
                                        variant: 'destructive',
                                      })
                                    )
                                }
                              >
                                Clear KV Store
                              </Button>
                            </DialogClose>
                            <DialogClose asChild>
                              <Button variant="default">Cancel</Button>
                            </DialogClose>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant={'destructive'} className="mt-4 w-full">
                        Delete Integration
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>
                          Delete <code className="bg-">{integration.name}</code> Integration
                        </DialogTitle>
                      </DialogHeader>
                      <DialogDescription>
                        Any devices connected to this integration will stop working. Are you sure
                        you want to permanently remove this integration?
                      </DialogDescription>
                      <DialogFooter className="grid gap-4">
                        <Button
                          variant="destructive"
                          type="submit"
                          onClick={() => {
                            deleteIntegration().then(() => {
                              navigate('/ihub');
                            });
                          }}
                        >
                          Delete Integration
                        </Button>
                        <DialogClose asChild>
                          <Button variant="default" onClick={() => {}}>
                            Cancel
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        <div className="col-span-4 flex flex-col overflow-hidden">
          <Tabs defaultValue="source" className="flex h-full flex-col">
            <TabsList className="h-10 shrink-0 rounded-none border-b">
              <TabsTrigger value="source">Source</TabsTrigger>
              <TabsTrigger value="synced">
                Synced
                {integration.syncedVersion && (
                  <span className="ml-1.5 text-xs text-gray-400">({integration.syncedVersion})</span>
                )}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="source" className="flex flex-grow flex-col data-[state=inactive]:hidden">
              <Suspense
                fallback={
                  <div className="flex flex-grow items-center justify-center">
                    <div className="flex w-fit flex-row gap-5 p-5 outline-gray-300">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={'animate-spin stroke-gray-50'}
                      >
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                      <span className="text-gray-200">Loading Editor</span>
                    </div>
                  </div>
                }
              >
                <div className="flex flex-grow flex-col pt-2">
                  <MonacoEditor id={id ?? ''} uploadCode={uploadCode} setCurrentCode={setCurrentCode} />
                </div>
              </Suspense>
            </TabsContent>
            <TabsContent value="synced" className="flex flex-grow flex-col data-[state=inactive]:hidden">
              {integration.syncedCompiledCode ? (
                <ReadOnlyCodeViewer value={integration.syncedCompiledCode} />
              ) : (
                <div className="flex flex-grow items-center justify-center text-gray-600">
                  No synced code available
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
      <Dialog open={!!selectedVariable} onOpenChange={() => setSelectedVariable(null)}>
        <DialogContent className="p-0">
          <DialogHeader className="border-b border-[#2f3a37] p-6">
            <DialogTitle>Edit Variable</DialogTitle>
          </DialogHeader>
          {selectedVariable && (
            <>
              <div className="space-y-6 p-6">
                <div className="space-y-2">
                  <label>
                    <p>Variable Name</p>
                    <input
                      type="text"
                      value={selectedVariable.name}
                      onChange={(e) =>
                        setSelectedVariable({
                          ...selectedVariable,
                          name: e.target.value,
                        })
                      }
                      className="w-full rounded-md border bg-background px-3 py-2"
                    />
                  </label>
                  <label>
                    <p>Data Type</p>
                    <Select
                      value={selectedVariable.dataType.replace(/\[\]$/, '')}
                      onValueChange={(base) => {
                        const isArray = /\[\]$/.test(selectedVariable.dataType);
                        const nextType = isArray ? `${base}[]` : base;
                        setSelectedVariable({
                          ...selectedVariable,
                          dataType: nextType,
                          options:
                            base === 'select'
                              ? sanitizeOptions(selectedVariable.options) ?? []
                              : undefined,
                        });
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="string">
                          <span className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('string')}`}>
                            String
                          </span>
                        </SelectItem>
                        <SelectItem value="number">
                          <span className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('number')}`}>
                            Number
                          </span>
                        </SelectItem>
                        <SelectItem value="boolean">
                          <span className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('boolean')}`}>
                            Boolean
                          </span>
                        </SelectItem>
                        <SelectItem value="password">
                          <span className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('password')}`}>
                            Password
                          </span>
                        </SelectItem>
                        <SelectItem value="select">
                          <span className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('select')}`}>
                            Select
                          </span>
                        </SelectItem>
                        <SelectItem value="uri">
                          <span className={`rounded-full px-2 py-0.5 ${getTypeTagStyle('uri')}`}>
                            URI
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </label>
                  <label>
                    <p>Description (Optional)</p>
                    <input
                      type="text"
                      value={selectedVariable.description || ''}
                      onChange={(e) =>
                        setSelectedVariable({
                          ...selectedVariable,
                          description: e.target.value,
                        })
                      }
                      className="w-full rounded-md border bg-background px-3 py-2"
                    />
                  </label>
                  <label>
                    <p>Documentation URL (Optional)</p>
                    <input
                      type="text"
                      value={selectedVariable.documentation || ''}
                      onChange={(e) =>
                        setSelectedVariable({
                          ...selectedVariable,
                          documentation: e.target.value,
                        })
                      }
                      className="w-full rounded-md border bg-background px-3 py-2"
                    />
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="edit-var-multiple"
                        checked={/\[\]$/.test(selectedVariable.dataType)}
                        onCheckedChange={(val) =>
                          setSelectedVariable((prev) => {
                            if (!prev) return prev;
                            const base = prev.dataType.replace(/\[\]$/, '');
                            return {
                              ...prev,
                              dataType: val === true ? `${base}[]` : base,
                            };
                          })
                        }
                      />
                      <Label htmlFor="edit-var-multiple">Multiple</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="edit-var-optional"
                        checked={selectedVariable.optional === true}
                        onCheckedChange={(val) =>
                          setSelectedVariable((prev) => (prev ? { ...prev, optional: val === true } : prev))
                        }
                      />
                      <Label htmlFor="edit-var-optional">Optional</Label>
                    </div>
                  </div>
                  {selectedVariable.dataType === 'select' && (
                    <label>
                      <p>Options</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editOptionInput}
                          onChange={(e) => setEditOptionInput(e.target.value)}
                          onKeyDown={handleEditOptionKeyDown}
                          placeholder="Add a selectable option"
                          className="w-full rounded-md border bg-background px-3 py-2"
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={handleAddEditOption}
                          disabled={!editOptionInput.trim()}
                        >
                          Add
                        </Button>
                      </div>
                      {selectedVariable.options && selectedVariable.options.length > 0 && (
                        <div className="space-y-2 pt-2">
                          {(selectedVariable.options ?? []).map((option, index) => (
                            <div
                              key={option}
                              draggable
                              onDragStart={() => handleEditOptionDragStart(index)}
                              onDragOver={(event) => handleEditOptionDragOver(event, index)}
                              onDragEnd={handleEditOptionDragEnd}
                              className={`flex items-center gap-3 rounded-lg border border-[#3a4b45] bg-[#2e3835] px-3 py-1.5 transition-colors ${
                                draggingEditOptionIdx === index
                                  ? 'border-lime-400 bg-[#385043] cursor-grabbing'
                                  : 'cursor-pointer hover:border-[#4b5d58]'
                              }`}
                            >
                              <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
                              <span className="flex-1 text-sm text-gray-100">{option}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveEditOption(option)}
                                tooltip="Remove option"
                              >
                                <Trash2 className="h-4 w-4 text-red-400" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </label>
                  )}
                  {selectedVariable.dataType.replace(/\[\]$/, '') === 'uri' && (
                    <div className="space-y-3">
                      <div>
                        <p className="mb-2">Allowed Protocols</p>
                        <p className="mb-3 text-xs text-gray-400">
                          Leave empty to allow all common protocols
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {COMMON_URI_PROTOCOLS.map((proto) => {
                            const isSelected = (selectedVariable.options ?? []).includes(proto.value);
                            return (
                              <button
                                key={proto.value}
                                type="button"
                                onClick={() => {
                                  setSelectedVariable((prev) => {
                                    if (!prev) return prev;
                                    const current = prev.options ?? [];
                                    if (isSelected) {
                                      return {
                                        ...prev,
                                        options: current.filter((p) => p !== proto.value),
                                      };
                                    } else {
                                      return {
                                        ...prev,
                                        options: [...current, proto.value],
                                      };
                                    }
                                  });
                                }}
                                className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                                  isSelected
                                    ? 'border-teal-500 bg-teal-500/20 text-teal-300'
                                    : 'border-[#3a4b45] bg-[#2e3835] text-gray-300 hover:border-[#4b5d58]'
                                }`}
                              >
                                {proto.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <p className="mb-2">Custom Protocol</p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={editOptionInput}
                            onChange={(e) => setEditOptionInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const trimmed = editOptionInput.trim();
                                if (trimmed && trimmed.includes('://')) {
                                  // Extract just the protocol part (e.g., "https://" from "https://example.com")
                                  const match = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)/);
                                  const protocol = match ? match[1].toLowerCase() : null;
                                  if (protocol) {
                                    setSelectedVariable((prev) => {
                                      if (!prev) return prev;
                                      const current = prev.options ?? [];
                                      if (current.includes(protocol)) return prev;
                                      return {
                                        ...prev,
                                        options: [...current, protocol],
                                      };
                                    });
                                  }
                                  setEditOptionInput('');
                                }
                              }
                            }}
                            placeholder="mqtt:// or mqtt://broker.local"
                            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              const trimmed = editOptionInput.trim();
                              if (trimmed && trimmed.includes('://')) {
                                // Extract just the protocol part
                                const match = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)/);
                                const protocol = match ? match[1].toLowerCase() : null;
                                if (protocol) {
                                  setSelectedVariable((prev) => {
                                    if (!prev) return prev;
                                    const current = prev.options ?? [];
                                    if (current.includes(protocol)) return prev;
                                    return {
                                      ...prev,
                                      options: [...current, protocol],
                                    };
                                  });
                                }
                                setEditOptionInput('');
                              }
                            }}
                            disabled={!editOptionInput.trim() || !editOptionInput.includes('://')}
                          >
                            Add
                          </Button>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Enter protocol or paste URL to extract protocol</p>
                      </div>
                      {selectedVariable.options && selectedVariable.options.length > 0 && (
                        <div>
                          <p className="mb-2">Selected Protocols</p>
                          <div className="flex flex-wrap gap-1.5">
                            {(selectedVariable.options ?? []).map((option, index) => (
                              <div
                                key={option}
                                draggable
                                onDragStart={() => handleEditOptionDragStart(index)}
                                onDragOver={(event) => handleEditOptionDragOver(event, index)}
                                onDragEnd={handleEditOptionDragEnd}
                                className={`flex items-center gap-1 rounded border border-[#3a4b45] bg-[#2e3835] pl-1.5 pr-0.5 py-0.5 transition-colors ${
                                  draggingEditOptionIdx === index
                                    ? 'border-teal-400 bg-[#1a3a35] cursor-grabbing'
                                    : 'cursor-grab hover:border-[#4b5d58]'
                                }`}
                              >
                                <GripVertical className="h-3 w-3 text-gray-500" />
                                <span className="font-mono text-xs text-gray-100">{option}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveEditOption(option)}
                                  className="p-0.5 hover:bg-red-500/20 rounded"
                                >
                                  <X className="h-3 w-3 text-red-400" />
                                </button>
                              </div>
                            ))}
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            Drag to reorder. First protocol is the default.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="flex flex-col-reverse gap-3 border-t border-[#2f3a37] bg-[#111]/30 p-6 sm:flex-row sm:justify-end">
                <Button variant="ghost" onClick={() => setSelectedVariable(null)}>
                  Cancel
                </Button>
                <Button
                  variant="ghost"
                  className="text-red-400 hover:text-red-200"
                  size="sm"
                  onClick={() => {
                    const remainingFields = integrationResponse.integration.integrationFields
                      .filter((v) => v.id !== selectedVariable.id)
                      .map((field) => ({
                        ...field,
                        description: field.description || undefined,
                        documentation: field.documentation || undefined,
                        options:
                          field.dataType === 'select'
                            ? sanitizeOptions(field.options) ?? []
                            : sanitizeOptions(field.options),
                      }));

                    updateIntegrationFields({
                      integrationFields: remainingFields,
                    })
                      .then(() => toast({ title: 'Updated fields' }))
                      .catch((ex) => {
                        toast({
                          title: 'Could not update fields',
                          variant: 'destructive',
                          description: ex.message,
                        });
                      });
                    setSelectedVariable(null);
                  }}
                >
                  Remove
                </Button>
                <Button onClick={() => handleUpdateVariable(selectedVariable)}>Save Changes</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
export default IntegrationPage;
